import { Page, PageBody, PageHeader } from "@/components/app-layout";
import { KitDeletionDialog } from "@/components/dependency-deletion/KitDeletionDialog";
import { KitDisplayName } from "@/components/display/display-name/KitDisplayName";
import { useGroupUuid } from "@/hooks/use-group-uuid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEdit,
  faTrash,
} from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  GroupedTable,
  GroupedTableColumn,
} from "../components/GroupedTable/GroupedTable";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { useKitsData } from "../data/use-kits-data";
import { useGroupSlug } from "../hooks/use-group-slug";
import { groupByDate, smartDate } from "../lib/date";
import { GetKitDto } from "../lib/gen-api";

export const ConversationsPage = () => {
  const groupUuid = useGroupUuid();
  const groupSlug = useGroupSlug();
  const { kits } = useKitsData(groupUuid, {
    type: "conversation",
  });
  const groupedKits = useMemo(() => groupByDate(kits, "date_updated"), [kits]);

  const columns: GroupedTableColumn<GetKitDto>[] = [
    { key: "name", label: "Name" },
    { key: "date_updated", label: "Last Modified" },
  ];

  return (
    <Page className="bg-background-1">
      <PageHeader>
        <PageHeader.Title>Chats</PageHeader.Title>
      </PageHeader>
      <PageBody>
        <GroupedTable
          isSearchable
          isSortable
          data={groupedKits}
          columns={columns}
          itemIdKey="uuid"
          renderCell={{
            name: (_, row) => <KitDisplayName kit={row} />,
            date_updated: (value) => smartDate(value),
          }}
          renderActions={({ row }) => <RowActions row={row} />}
          rowLink={(row) => ({
            to: "/w/$groupSlug/k/$kitUuid",
            params: { groupSlug, kitUuid: row.uuid },
          })}
          emptyMessage="Your conversations will show up here. Create your first to kick things off"
        />
      </PageBody>
    </Page>
  );
};

function RowActions({ row }: { row: GetKitDto }) {
  const groupUuid = useGroupUuid();
  const { updateKit } = useKitsData(groupUuid);
  const [open, setOpen] = useState(false);
  const [newName, setNewName] = useState(row.name);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setNewName(row.name);
  }, [row.name]);

  const handleEdit = async () => {
    setLoading(true);
    setError(null);

    try {
      await updateKit({ kitUuid: row.uuid, name: newName });
      setOpen(false);
      toast.success("Conversation name updated");
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (_) {
      setError("Failed to update name");
      toast.error("Failed to update name");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button variant="ghost" size="sm" aria-label="Edit">
            <FontAwesomeIcon icon={faEdit} />
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Conversation Name</DialogTitle>
            <DialogDescription>
              Change the name of this conversation. This will be visible to all
              workspace members.
            </DialogDescription>
          </DialogHeader>
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Conversation name"
            disabled={loading}
            className="mb-2"
            autoFocus
          />
          {error && <div className="mb-2 text-sm text-red-500">{error}</div>}
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setOpen(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleEdit}
              disabled={loading || !newName.trim()}
            >
              {loading ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <KitDeletionDialog kitUuid={row.uuid} groupUuid={row.group_uuid}>
        <Button
          variant="ghost"
          size="sm"
          className="hover:bg-destructive hover:text-destructive-foreground"
          aria-label="Delete"
          title="Delete"
        >
          <FontAwesomeIcon icon={faTrash} />
        </Button>
      </KitDeletionDialog>
    </>
  );
}
